import { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { CODE_FIELDS, previewPaste, readPastedCodes } from "./codexUtils";

const ACTIONS = {
  new: { label: "New", colour: "green" },
  update: { label: "Changes", colour: "orange" },
  same: { label: "Same", colour: "gray" },
  skip: { label: "Skipped · no long code", colour: "red" },
};
const label = (field) => CODE_FIELDS.find((f) => f.field === field)?.label || field;

/**
 * Paste codes copied from a spreadsheet (or CSV). Shows what would be added
 * and changed, matched by long code, before anything happens.
 */
const PasteCodesModal = ({ isOpen, onClose, codes, onApply }) => {
  const [text, setText] = useState("");
  const pasted = useMemo(() => readPastedCodes(text), [text]);
  const preview = useMemo(() => previewPaste(codes, pasted), [codes, pasted]);
  const counts = Object.fromEntries(Object.keys(ACTIONS).map((a) => [a, preview.filter((p) => p.action === a).length]));
  const close = () => {
    setText("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Paste codes from a spreadsheet</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={2}>
            Copy the rows in Excel (or paste CSV). If the first row has column names (Long code, Description, Filter…) they're used;
            otherwise the columns are long code, description, in use, filter, unit, type code, camel code. Codes are matched by long code.
          </Text>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"Long code\tDescription\nABC\tA description"}
            fontFamily="mono"
            fontSize="xs"
            rows={6}
            autoFocus
          />
          {preview.length > 0 && (
            <Box mt={4}>
              <HStack spacing={2} mb={2} flexWrap="wrap">
                <Text fontSize="sm" fontWeight="semibold">Columns:</Text>
                {pasted.columns.map((c) => <Badge key={c} textTransform="none">{label(c)}</Badge>)}
              </HStack>
              <HStack spacing={2} mb={2}>
                {Object.entries(ACTIONS).map(([a, v]) => counts[a] > 0 && (
                  <Badge key={a} colorScheme={v.colour} textTransform="none">{counts[a]} {v.label.toLowerCase()}</Badge>
                ))}
              </HStack>
              <Box maxH="320px" overflowY="auto" borderWidth="1px" borderRadius="md">
                <Table size="sm">
                  <Thead position="sticky" top={0} bg="white">
                    <Tr><Th />{pasted.columns.map((c) => <Th key={c}>{label(c)}</Th>)}</Tr>
                  </Thead>
                  <Tbody>
                    {preview.map((p, i) => (
                      <Tr key={i} opacity={p.action === "same" ? 0.6 : 1}>
                        <Td><Badge colorScheme={ACTIONS[p.action].colour} textTransform="none">{ACTIONS[p.action].label}</Badge></Td>
                        {pasted.columns.map((c) => {
                          const changed = p.action === "update" && c !== "longCode" && String(p.existing[c] ?? "") !== String(p.row[c] ?? "");
                          return (
                            <Td key={c} fontSize="xs" bg={changed ? "orange.50" : undefined}>
                              {changed && <Text as="span" color="gray.400" textDecoration="line-through" mr={1}>{p.existing[c]}</Text>}
                              {p.row[c]}
                            </Td>
                          );
                        })}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={close}>Cancel</Button>
          <Button
            colorScheme="blue"
            isDisabled={!counts.new && !counts.update}
            onClick={() => {
              onApply(preview);
              close();
            }}>
            Add {counts.new} · update {counts.update}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PasteCodesModal;
